// Orchestrator phase 2: đảm bảo gh CLI có và đã auth. Tự cài + tự login nếu cần.
//
// v1.2.4: Khi verify remote fail → KHÔNG throw văng. Call recovery handler
// prompt user switch account / retry / abort.
//
// v1.2.5: gh CLI install fail + gh auth login fail giờ cũng có recovery prompt
// (loop retry / skip / abort) thay vì throw thẳng.
import { checkGhCliAuthStatus } from "./check-gh-cli-auth-status.js";
import { detectPackageManager } from "./detect-package-manager.js";
import { handleRemoteAccessFailureWithAccountSwitch } from "./handle-remote-access-failure-with-account-switch.js";
import { installGhCliViaPackageManager } from "./install-gh-cli-via-package-manager.js";
import {
  promptRetryOrSkip,
  UserAbortedRecoveryError,
} from "./prompt-recovery-action-on-failure.js";
import { setupGitCredentialViaGh } from "./setup-git-credential-via-gh.js";
import { log } from "./terminal-logger.js";
import { triggerGhCliAuthLogin } from "./trigger-gh-cli-auth-login.js";
import { tryVerifyGitRemoteAccessible } from "./verify-git-remote-accessible.js";

// Gọi trước mọi flow cần GitHub (init nhánh 1, nhánh 2-create-remote, nhánh 3).
// remoteUrl optional — nếu truyền sẽ verify access cụ thể.
// v1.2.5: Loop retry cho install + auth steps — user luôn có choice.
// v1.2.7: Return resolvedRemoteUrl — user có thể đã re-nhập URL khác trong recovery menu.
//        Caller dùng URL này thay vì URL gốc cho các bước tiếp theo.
export async function ensureGitHubReady(
  remoteUrl?: string,
): Promise<{ resolvedRemoteUrl?: string }> {
  // === Step 1: Ensure gh CLI installed (loop retry) ===
  while (checkGhCliAuthStatus() === "not-installed") {
    log.warn("gh CLI chưa cài. Avatar sẽ tự cài.");
    const pm = detectPackageManager();
    if (!pm) {
      // Không có PM → user phải cài tay. Loop prompt cho retry sau khi cài.
      const action = await promptRetryOrSkip({
        taskName: "Phát hiện package manager",
        reason: "Không tìm thấy brew/apt/dnf/pacman/winget trên máy.",
        allowSkip: false,
        hint: "Cài gh CLI tay (https://cli.github.com) rồi chọn Retry.",
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước cài gh CLI.");
      }
      continue; // Retry → re-check status loop
    }

    try {
      installGhCliViaPackageManager(pm);
    } catch (err) {
      const action = await promptRetryOrSkip({
        taskName: `Cài gh CLI qua ${pm}`,
        reason: (err as Error).message,
        allowSkip: false,
        hint: "Cài tay (https://cli.github.com) rồi chọn Retry, hoặc Abort.",
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước cài gh CLI.");
      }
      // Retry → loop tiếp re-check installed.
    }
  }

  // === Step 2: Ensure gh CLI authenticated (loop retry) ===
  while (checkGhCliAuthStatus() === "not-authenticated") {
    log.warn("Chưa đăng nhập GitHub.");
    try {
      triggerGhCliAuthLogin();
    } catch (err) {
      const action = await promptRetryOrSkip({
        taskName: "Đăng nhập GitHub qua gh",
        reason: (err as Error).message,
        allowSkip: false,
        hint: "Thử lại — chọn cách login khác (browser vs token) khi gh prompt.",
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước gh auth login.");
      }
      continue;
    }
    // Re-check sau attempt — nếu vẫn fail, prompt retry.
    if (checkGhCliAuthStatus() !== "authenticated") {
      const action = await promptRetryOrSkip({
        taskName: "Verify gh auth",
        reason: "Sau gh auth login vẫn báo not-authenticated.",
        allowSkip: false,
        hint: "Có thể user cancel browser. Thử lại hoặc abort.",
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước verify gh auth.");
      }
    }
  }

  log.success("gh CLI sẵn sàng");

  // Đảm bảo git CLI dùng gh token cho HTTPS operations. Idempotent — chạy
  // mỗi lần init OK. Fix lỗi `git ls-remote exit 128` khi gh đã auth nhưng
  // git chưa biết.
  setupGitCredentialViaGh();

  if (remoteUrl) {
    const result = tryVerifyGitRemoteAccessible(remoteUrl);
    if (result.ok) {
      log.success(`Remote accessible: ${remoteUrl}`);
      return { resolvedRemoteUrl: remoteUrl };
    }
    // Recovery handler — loop menu cho user re-input URL / switch account / retry / abort.
    // Throws RemoteAccessAbortedError nếu user abort (init.ts catch exit 0).
    const recovered = await handleRemoteAccessFailureWithAccountSwitch({
      url: remoteUrl,
      initialReason: result.reason ?? "unknown",
      initialDetail: result.detail,
    });
    return { resolvedRemoteUrl: recovered.resolvedUrl };
  }
  return {};
}
